import { useState } from 'react'

import { type DocumentPreviewInput, isVet, type TreatmentWithRelations } from '@snootsvet/shared'

import { FormField } from '@/frontend/components/form/fields'
import { StaffSearch } from '@/frontend/components/staff-search'
import { Alert, AlertDescription } from '@/frontend/components/ui/alert'
import { Button } from '@/frontend/components/ui/button'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/frontend/components/ui/dialog'
import { useDownloadDocument } from '@/frontend/features/documents/use-download-document'
import { usePreviewDocument } from '@/frontend/features/documents/use-preview-document'
import { useGetAuthenticatedStaff } from '@/frontend/hooks/api/use-get-authenticated-staff'

/** Certifies one vaccination from its records, with blanks for anything not recorded. */
export function VaccineCertificateForm({
  vaccination,
  onCancel,
}: {
  vaccination: TreatmentWithRelations
  onCancel: () => void
}) {
  const { clearPreview, preview, previewDocument } = usePreviewDocument()
  const { downloadDocument } = useDownloadDocument()
  const { data: currentUser } = useGetAuthenticatedStaff()

  const defaultVetId =
    (isVet(vaccination.staff) ? vaccination.staffId : null) ??
    (currentUser && isVet(currentUser) ? currentUser.id : null)
  // undefined until staff pick someone, so the default applies once the signed-in user loads.
  const [chosenVetId, setVetId] = useState<string | null>()
  const [error, setError] = useState<string | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)

  const vetId = chosenVetId === undefined ? defaultVetId : chosenVetId
  const request: DocumentPreviewInput | null = !vetId
    ? null
    : {
        documentType: 'vaccination-certificate',
        input: { treatmentId: vaccination.id, vetId },
      }

  async function submit(action: 'preview' | 'download') {
    if (!request || isSubmitting) return
    setError(null)
    setIsSubmitting(true)
    try {
      await (action === 'download' ? downloadDocument(request) : previewDocument(request))
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Failed to generate certificate')
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div className={`max-w-full ${preview ? 'w-5xl' : 'w-lg'}`}>
      <DialogHeader className="mb-4 flex-row items-center justify-between">
        <DialogTitle>Vaccination Certificate</DialogTitle>
        {preview && (
          <Button type="button" variant="outline" onClick={clearPreview}>
            Back
          </Button>
        )}
      </DialogHeader>
      {error && (
        <Alert variant="destructive" className="mb-4">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}
      {preview ? (
        <div className="flex h-[75vh] min-h-0 flex-col gap-4">
          <iframe
            title="Vaccination certificate preview"
            src={preview.url}
            className="min-h-0 w-full flex-1 rounded border"
          />
        </div>
      ) : (
        <div className="flex flex-col gap-4">
          <p className="text-muted-foreground">
            {vaccination.productInfo.name}, filled in from the vaccination record. Anything not
            recorded is left blank to fill in by hand.
          </p>
          <FormField
            label="Signing vet"
            required
            disabled={isSubmitting}
            render={({ id, disabled }) => (
              <StaffSearch
                id={id}
                value={vetId}
                onChange={(staff) => setVetId(staff?.id ?? null)}
                filter={isVet}
                placeholder="Select vet..."
                allowClear
                disabled={disabled}
              />
            )}
          />
          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={onCancel}>
              Cancel
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={() => submit('download')}
              disabled={!request || isSubmitting}
            >
              Download PDF
            </Button>
            <Button
              type="button"
              onClick={() => submit('preview')}
              disabled={!request || isSubmitting}
            >
              Preview PDF
            </Button>
          </div>
        </div>
      )}
    </div>
  )
}

export function VaccineCertificateDialog({
  vaccination,
  open,
  onOpenChange,
}: {
  vaccination: TreatmentWithRelations
  open: boolean
  onOpenChange: (open: boolean) => void
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        showCloseButton={false}
        className="w-fit grid-cols-[minmax(0,1fr)] sm:max-w-[calc(100%-2rem)]"
      >
        <VaccineCertificateForm vaccination={vaccination} onCancel={() => onOpenChange(false)} />
      </DialogContent>
    </Dialog>
  )
}
